import { EventBus, IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, computed, makeAutoObservable, observable } from 'mobx';

import type {
  AccessDto,
  AccountDto,
  RoleDto,
  ScopeDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';

import { GetRolesCollaboration } from '../../application/collaborations/get-roles.collaboration';
import { GetScopesCollaboration } from '../../application/collaborations/get-scopes.collaboration';
import { GetUsersByScopeCollaboration } from '../../application/collaborations/get-users-by-scope.collaboration';
import { CreatedAccountEvent, CreateUserViewModel } from './CreateUser.vm';

const RECOGNITION_DEFAULT_SCOPE =
  process.env.REACT_APP_RECOGNITION_DESCOMPLICA_IO_SCOPE || 'descomplica-io-scope';

@ViewModel({ singleton: true })
export class UsersViewModel implements IReactiveModel {
  @observable totalUsers = 0;
  @observable users: AccountDto[] = [];
  @observable scopes: ScopeDto[] = [];
  @observable roles: RoleDto[] = [];
  @observable selectedScopeGuid: string = RECOGNITION_DEFAULT_SCOPE;
  @observable selectedRoleGuid = '';
  @observable isLoading = false;
  @observable hasError = false;

  constructor(
    private eventBus: EventBus,
    private getUsersByScopeCollaboration: GetUsersByScopeCollaboration,
    private getScopesCollaboration: GetScopesCollaboration,
    private getRolesCollaboration: GetRolesCollaboration,
    private createUserViewModel: CreateUserViewModel,
  ) {
    makeAutoObservable(this);

    this.eventBus.on(CreatedAccountEvent, this.handleCreatedUser.bind(this));
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;

      const [{ data: users }, { data: scopes }, { data: roles }] = await Promise.all([
        this.getUsersByScopeCollaboration.run(this.selectedScopeGuid),
        this.getScopesCollaboration.run(),
        this.getRolesCollaboration.run(),
      ]);

      this.totalUsers = users.length;
      this.scopes = scopes;
      this.roles = roles;
      this.selectedRoleGuid = roles[0]?.guid || '';
      this.users = users.filter((account) =>
        account.accessList.some(
          (access: AccessDto) =>
            access.role.guid === this.selectedRoleGuid &&
            access.scope.guid === this.selectedScopeGuid,
        ),
      );
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  handleCreatedUser(createdAccountEvent: CreatedAccountEvent) {
    this.users = [createdAccountEvent.user, ...this.users];
  }

  @action
  async getUsersByScopeAndRole() {
    try {
      this.isLoading = true;

      const { data } = await this.getUsersByScopeCollaboration.run(this.selectedScopeGuid);

      this.users = data.filter((account) =>
        account.accessList.some(
          (access: AccessDto) =>
            access.role.guid === this.selectedRoleGuid &&
            access.scope.guid === this.selectedScopeGuid,
        ),
      );
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  setScope(scopeGuid: string): void {
    this.selectedScopeGuid = scopeGuid;

    this.getUsersByScopeAndRole();
  }

  @action
  setRole(roleGuid: string): void {
    this.selectedRoleGuid = roleGuid;

    this.getUsersByScopeAndRole();
  }

  @action
  openCreateModal() {
    this.createUserViewModel.openCreateModal();
  }

  @action
  closeCreateModal() {
    this.createUserViewModel.closeCreateModal();
  }

  @computed
  get isCreateModalOpen() {
    return this.createUserViewModel.isCreateModalOpen;
  }
}
